const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const EMPTY_VALUE = '—';

/**
 * BigInt-поля API (`durationMs`, `fileSizeBytes`) приходят десятичной строкой.
 * Разбираем их в BigInt и считаем в целых числах — без приведения к Number,
 * которое теряет точность на больших значениях.
 */
function parseBigIntString(value: string | null | undefined): bigint | null {
  if (value === null || value === undefined || !/^\d+$/u.test(value)) {
    return null;
  }
  try {
    return BigInt(value);
  } catch {
    return null;
  }
}

export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }
  return DATE_TIME_FORMATTER.format(date);
}

const pad = (value: bigint): string => value.toString().padStart(2, '0');

/** Длительность из целых миллисекунд: `ч:мм:сс` либо `мм:сс`. */
export function formatDurationMs(value: string | null | undefined): string {
  const milliseconds = parseBigIntString(value);
  if (milliseconds === null) {
    return EMPTY_VALUE;
  }
  const totalSeconds = milliseconds / 1000n;
  const hours = totalSeconds / 3600n;
  const minutes = (totalSeconds % 3600n) / 60n;
  const seconds = totalSeconds % 60n;
  return hours > 0n ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** Длительность из числа миллисекунд (таймер этапа). */
export function formatElapsedMs(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    return EMPTY_VALUE;
  }
  return formatDurationMs(String(Math.floor(milliseconds)));
}

/** Длительность между двумя ISO-датами; если нет finishedAt — до nowMs. */
export function formatIntervalMs(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  nowMs: number = Date.now(),
): string | null {
  if (startedAt === null || startedAt === undefined || startedAt === '') {
    return null;
  }
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) {
    return null;
  }
  const end =
    finishedAt !== null && finishedAt !== undefined && finishedAt !== ''
      ? new Date(finishedAt).getTime()
      : nowMs;
  if (Number.isNaN(end) || end < start) {
    return null;
  }
  return formatElapsedMs(end - start);
}

/** Длительность этапа: «42 сек», «2 мин 36 сек», «1 ч 12 мин». */
export function formatStageDurationRu(milliseconds: number): string | null {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    return null;
  }
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours} ч ${minutes} мин` : `${hours} ч`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes} мин ${seconds} сек` : `${minutes} мин`;
  }
  return `${seconds} сек`;
}

/** Длительность между ISO-датами в человекочитаемом виде для панели этапов. */
export function formatStageIntervalRu(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  nowMs: number = Date.now(),
): string | null {
  if (startedAt === null || startedAt === undefined || startedAt === '') {
    return null;
  }
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) {
    return null;
  }
  const end =
    finishedAt !== null && finishedAt !== undefined && finishedAt !== ''
      ? new Date(finishedAt).getTime()
      : nowMs;
  if (Number.isNaN(end) || end < start) {
    return null;
  }
  return formatStageDurationRu(end - start);
}

const SIZE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'] as const;

export function formatFileSize(value: string | null | undefined): string {
  const bytes = parseBigIntString(value);
  if (bytes === null) {
    return EMPTY_VALUE;
  }
  if (bytes < 1024n) {
    return `${bytes} ${SIZE_UNITS[0]}`;
  }

  let unitIndex = 0;
  let scale = 1n;
  while (unitIndex < SIZE_UNITS.length - 1 && bytes / (scale * 1024n) > 0n) {
    scale *= 1024n;
    unitIndex += 1;
  }

  const tenths = (bytes * 10n) / scale;
  const whole = tenths / 10n;
  const fraction = tenths % 10n;
  const unit = SIZE_UNITS[unitIndex] ?? '';
  return whole >= 10n ? `${whole} ${unit}` : `${whole},${fraction} ${unit}`;
}

export function initialsOf(source: string | null | undefined): string {
  if (source === null || source === undefined) {
    return EMPTY_VALUE;
  }
  const parts = source
    .trim()
    .split(/[\s@._-]+/u)
    .filter((part) => part !== '');
  if (parts.length === 0) {
    return EMPTY_VALUE;
  }
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts.length > 1 ? (parts[1]?.charAt(0) ?? '') : '';
  return `${first}${second}`.toUpperCase();
}

/**
 * Инициалы аватара: из имени (слова), иначе первая буква email.
 */
export function avatarInitials(
  name: string | null | undefined,
  email: string | null | undefined,
): string {
  const trimmedName = name?.trim() ?? '';
  if (trimmedName !== '') {
    return initialsOf(trimmedName);
  }
  const trimmedEmail = email?.trim() ?? '';
  if (trimmedEmail !== '') {
    return trimmedEmail.charAt(0).toUpperCase();
  }
  return EMPTY_VALUE;
}
